import React from "react";
import { Link } from "react-router-dom";
import SiteShell from "../../components/layout/SiteShell";
import ContentPageLayout from "../../components/content/ContentPageLayout";
import SEO from "../../components/seo/SEO";
import PageFaq from "../../components/content/PageFaq";
import ConversionCtas from "../../components/content/ConversionCtas";
import { buildProductJsonLd, buildFaqPageJsonLd } from "../../lib/seoJsonLd";
import { HERO_IMAGE } from "../../lib/data";
import { PHOTO_BRIEF } from "../../lib/contentImagePlaceholders";

const goldFaqs = [
  {
    question: "What makes Gold Sea Moss different?",
    answer:
      "Gold sea moss from MossMatrix is our smooth, mild classic base: mineral-rich and commonly used in daily wellness routines. People often describe it as the easiest first spoonful if you are new to sea moss gel.",
  },
  {
    question: "Can I add herbs to a gold base?",
    answer:
      "Custom blends are being simplified right now. Start with plain Gold, and explore mix-in ideas for education. Selected mix-ins may return later. Trial pouches and smaller formats stay plain.",
  },
  {
    question: "How much gold gel should I use?",
    answer:
      "Many customers start with a small spoonful and adjust to taste, often one to two tablespoons stirred into drinks or oats. That is a routine suggestion, not medical advice. Speak with a professional if you need personalised guidance.",
  },
  {
    question: "Is gold ‘stronger’ than purple?",
    answer:
      "Neither is a medical upgrade over the other. It is a taste and colour choice. Gold tends to feel milder; purple reads bolder on the palate. Rotate if you like variety.",
  },
];

export default function GoldSeaMossGelPage() {
  const path = "/products/gold-sea-moss-gel";
  const productLd = buildProductJsonLd({
    name: "Gold Sea Moss Gel",
    description:
      "Wildcrafted gold sea moss gel with spring water: smooth, mild, mineral-rich, and commonly stirred into drinks, oats, or cooled tea as part of a balanced lifestyle.",
    urlPath: path,
    image: HERO_IMAGE,
    lowPrice: "12.99",
    highPrice: "34.99",
    offerCount: "6",
  });

  return (
    <SiteShell>
      <SEO
        title="Gold Sea Moss Gel | Smooth, mild and mineral-rich"
        description="MossMatrix Gold Sea Moss Gel: classic wildcrafted base, spring water, optional powdered botanicals on 500 ml and 1 litre jars. 300 ml jar and 250 ml pouch are plain. How to use, sizes, and honest FAQs."
        path={path}
        ogType="product"
        jsonLd={[productLd, buildFaqPageJsonLd(goldFaqs)]}
      />
      <ContentPageLayout
        eyebrow="Product"
        title="Gold Sea Moss Gel"
        subtitle="Our classic sea moss base: smooth, mild, and mineral-rich. Traditionally used in coastal cuisines; today it is popular in daily wellness routines stirred into drinks and bowls."
      >
        <div className="grid lg:grid-cols-2 gap-10 items-start mb-14">
          <div>
            <div className="rounded-[2rem] overflow-hidden border border-cream aspect-[4/5] max-h-[480px]">
              <img src={HERO_IMAGE} alt="MossMatrix gold sea moss gel" className="w-full h-full object-cover editorial-img" />
            </div>
            <p className="mt-2 text-[10px] text-botanical/50 leading-relaxed">{PHOTO_BRIEF.jarGoldCloseup}</p>
          </div>
          <div className="space-y-6">
            <section className="rounded-2xl border border-cream bg-white p-6">
              <h2 className="font-serif text-xl text-forest">What makes Gold Sea Moss different?</h2>
              <p className="mt-3 text-sm text-botanical/85 leading-relaxed">
                Gold reads gentle on the spoon: a warm, classic profile that pairs well with spices, botanicals, and simple drinks. It is the base we suggest if you want something easy to fold into an existing habit without overpowering other tastes.
              </p>
            </section>
            <div className="flex flex-wrap gap-3">
              <Link to="/#builder" className="rounded-full bg-forest text-cream px-6 py-3 text-sm hover:bg-botanical transition">
                Build a Gold blend
              </Link>
              <Link to="/learn/gold-vs-purple-sea-moss" className="rounded-full border border-forest/20 px-6 py-3 text-sm hover:bg-cream transition">
                Gold vs Purple
              </Link>
              <Link to="/ingredients" className="rounded-full border border-forest/20 px-6 py-3 text-sm hover:bg-cream transition">
                Herb library
              </Link>
            </div>
          </div>
        </div>

        <section className="rounded-3xl border border-cream bg-white p-8 md:p-10 mb-10">
          <h2 className="font-serif text-2xl text-forest">How to use it</h2>
          <p className="mt-3 text-sm text-botanical/85 max-w-3xl leading-relaxed">
            Start small, taste often. Gold gel dissolves easily into <strong className="text-forest">cooled herbal tea</strong>,{" "}
            <strong className="text-forest">oats</strong>, <strong className="text-forest">plant milks</strong>, and light{" "}
            <strong className="text-forest">sauces</strong>. It may support variety in what you already eat. It does not replace balanced meals.
          </p>
          <p className="mt-3 text-xs text-botanical/60">{PHOTO_BRIEF.smoothiePrep}</p>
        </section>

        <section className="rounded-3xl border border-cream bg-offwhite p-8 md:p-10 mb-10">
          <h2 className="font-serif text-2xl text-forest">Available sizes</h2>
          <p className="mt-3 text-sm text-botanical/80 max-w-2xl">
            Foil pouches are our best-value packaging. Glass jars are premium and fridge-ready. Trial and 300 ml sizes are plain only. On 500 ml and
            1 litre you can stay plain or add up to three powdered botanicals at £1.50 each in the builder.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-botanical/85">
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">200 ml trial pouch</h3>
              <p className="mt-2">Plain £5.99</p>
            </div>
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">300 ml pouch</h3>
              <p className="mt-2">Plain £8.99</p>
            </div>
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">300 ml jar</h3>
              <p className="mt-2">Plain £10.99</p>
            </div>
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">500 ml pouch</h3>
              <p className="mt-2">Plain £14.99 · Custom from £16.49</p>
            </div>
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">500 ml jar</h3>
              <p className="mt-2">Plain £16.99 · Custom from £18.49</p>
            </div>
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">1 litre pouch</h3>
              <p className="mt-2">Plain £24.99 · Custom from £26.49</p>
            </div>
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">1 litre jar</h3>
              <p className="mt-2">Plain £27.99 · Custom from £29.49</p>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-cream bg-white p-8 md:p-10 mb-6">
          <h2 className="font-serif text-2xl text-forest">Best pairings</h2>
          <ul className="mt-4 grid sm:grid-cols-2 gap-3 text-sm text-botanical/85">
            <li className="rounded-xl border border-cream bg-offwhite px-4 py-3">• Cooled chai-style tea with cinnamon</li>
            <li className="rounded-xl border border-cream bg-offwhite px-4 py-3">• Porridge with warming botanicals</li>
            <li className="rounded-xl border border-cream bg-offwhite px-4 py-3">• Simple lemon and ginger drinks at home</li>
            <li className="rounded-xl border border-cream bg-offwhite px-4 py-3">• Light soups and savoury sauces</li>
          </ul>
          <p className="mt-4 text-xs text-botanical/55">{PHOTO_BRIEF.teaOats}</p>
        </section>

        <PageFaq items={goldFaqs} />
        <ConversionCtas />

        <p className="text-center text-sm text-botanical/70 mt-10">
          <Link to="/shop" className="underline hover:text-forest">
            Back to shop
          </Link>
          {" · "}
          <Link to="/learn/how-to-store-sea-moss-gel" className="underline hover:text-forest">
            Storage tips
          </Link>
          {" · "}
          <Link to="/faq" className="underline hover:text-forest">
            FAQ
          </Link>
        </p>
      </ContentPageLayout>
    </SiteShell>
  );
}
